import { defineStore } from 'pinia';
import { type ApiStudy, getStudy, type ImageModality, studyIdFromUrl } from '@/api/study/study';
import { type ApiCase, getCase } from '@/api/case';
import { type StopHandle, taggedLogger } from '@/util';
import { ref } from 'vue';

const log = taggedLogger('dicom-view');


export const useScanViewStore = defineStore('dicom-view', () => {
    const modality = ref<ImageModality | 'loading'>('loading');

    let stopLoading: StopHandle = () => {};

    return {
        modality,
        loadCase: async (caseId: number): Promise<void> => {
            stopLoading();
            modality.value = 'loading';

            const controller = new AbortController();
            stopLoading = () => controller.abort();

            try {
                const data = await _loadCaseData(caseId, controller.signal);
                if (data.study && data.study.sourceModality) {
                    modality.value = data.study.sourceModality;
                } else {
                    log.warning('Could not resolve source-modality for case %d', caseId)
                }
            } catch (error) {
                if (controller.signal.aborted) {
                    log.info('Case loading aborted')
                } else {
                    throw error;
                }
            }
        },
        stop() {
            stopLoading();
        },
    }
});

type _ApiData = {
    case: ApiCase;
    study?: ApiStudy;
}

async function _loadCaseData(caseId: number, signal: AbortSignal): Promise<_ApiData> {
    log.info('Loading case %d for dicom-view', caseId);
    const case_ = await getCase(caseId, { signal });

    if (!case_.study) {
        log.warning('Case %d for dicom-view had no study', caseId)
        return { case: case_ };
    }

    log.info('Loading study %d for dicom-view', studyIdFromUrl(case_.study).study);
    const study = await getStudy(case_.study, { signal });

    return {
        case: case_,
        study,
    };
}
